<template>
  <div class="location-groups" @touchend="fixActionRestriction">
    <Container
      class="location-groups__container d-flex flex-column mb-4"
      v-protected
      tag="div"
      lock-axis="y"
      :drop-placeholder="{
        animationDuration: '150',
        showOnTop: true,
      }"
      @drop="handleDrop($event)"
    >
      <Draggable
        v-for="item in visibleGroups"
        :key="item.id"
        tag="div"
        class="location-groups__item d-flex"
      >
        <ui-icon
          class="mr-2"
          name="stacked-lines"
          size="24"
        />

        <template v-if="item.id">
          <a
            v-if="item.status !== 'NOT_ACTIVE'"
            href="javascript:void(0)"
            @click="openQr(item)"
            class="text-dark ml-2"
          >
            <ui-icon
              name="qr-code"
              size="24"
            />
          </a>
          <ui-icon
            v-else
            name="qr-code"
            size="24"
            class="ml-2"
          />
        </template>

        <strong class="ml-2">
          {{ item.name }}
        </strong>

        <ui-action-down
          :items="formActions"
          value-key="id"
          class="ml-auto"
          @select="handleAction($event, item)"
        />
      </Draggable>
    </Container>

    <ui-button
      outline
      type="primary"
      size="sm"
      native-type="button"
      class="w-100"
      @click="createGroup"
    >
      {{ $t('form.buttons.addGroup') }}
    </ui-button>

    <validation-provider
      tag="div"
      rules="required|min_value:2"
      mode="passive"
      v-slot="{ errors }"
    >
      <ui-input
        v-model="visibleGroups.length"
        type="hidden"
        :error="errors.length ? 'Please enter at least 2 groups' : null"
        class="mt-1"
      />
    </validation-provider>

    <location-group-form
      v-model="formShow"
      :group="formGroup"
      :location="location"
      @update:group="applyForm"
    />

    <qr-code
      v-if="location.id"
      :show="Boolean(QrShow)"
      v-bind="QrShow"
      @closed="closeQr"
    />
  </div>
</template>

<script>
import { Container, Draggable } from 'vue-smooth-dnd';
import applyDrag from '@/utils/applyDrag';
import QrCode from '@/components/QrCode.vue';
import LocationGroupForm from './LocationGroupForm.vue';

export default {

  components: {
    Container,
    Draggable,
    LocationGroupForm,
    QrCode,
  },

  props: {
    groups: {
      type: Array,
      required: true,
    },
    location: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    groupList: [],
    formShow: false,
    formGroup: null,
    QrShow: false,
  }),

  computed: {
    formActions() {
      return [
        {
          id: 'edit',
          label: this.$t('form.buttons.editGroup'),
        },
        {
          id: 'remove',
          label: this.$t('form.buttons.deleteGroup'),
        },
      ];
    },
    visibleGroups() {
      return this.groupList
        .filter(({ removed }) => !removed)
        .sort((a, b) => a.showOrder - b.showOrder);
    },
  },

  watch: {
    groups: {
      immediate: true,
      handler(value) {
        if (!value?.length) {
          return;
        }

        this.groupList = value;
      },
    },
    groupList: {
      deep: true,
      handler(value) {
        this.$emit('update:groups', value);
      },
    },
  },

  methods: {
    fixActionRestriction() {
      // issue: https://github.com/kutlugsahin/vue-smooth-dnd/issues/134
      document.body.classList.remove(
        'smooth-dnd-no-user-select',
        'smooth-dnd-disable-touch-action',
      );
    },
    handleDrop(event) {
      this.groupList = applyDrag(this.groupList, event, 'showOrder', 'removed');
    },
    openForm(item = null) {
      this.formGroup = item;
      this.formShow = true;
    },
    createGroup() {
      this.openForm();
    },
    async removeGroup(item) {
      if (item.staffs?.length > 0) {
        this.$alert({
          title: this.$t('staffs.modal.confirmation'),
          message: this.$t('staffs.modal.pleaseDeactivate'),
          applyButton: this.$t('form.buttons.ok'),
        });

        return;
      }

      if (
        await this.$confirm({
          title: this.$t('staffs.modal.confirmation'),
          message: this.$t('staffs.modal.sureDeleteGroup'),
          applyButton: this.$t('form.buttons.deleteGroup'),
          closeButton: this.$t('form.buttons.cancel'),
        })
      ) {
        if (!item.id) {
          this.groupList = this.groupList.filter((group) => group !== item);
        } else {
          item.removed = true;
          item.showOrder = null;
        }
      }
    },
    applyForm(group) {
      if (this.formGroup) {
        Object.assign(this.formGroup, group);
      } else {
        this.groupList.push({
          ...group,
          showOrder: this.groupList?.length || 0,
          newest: true,
          removed: false,
        });
      }

      this.formShow = false;
    },
    handleAction(event, item) {
      if (event === 'edit') {
        this.openForm(item);
      }

      if (event === 'remove') {
        this.removeGroup(item);
      }
    },
    openQr(item) {
      this.QrShow = {
        locationId: this.location.id,
        groupId: item.id,
      };
    },
    closeQr() {
      this.QrShow = null;
    },
  },
};
</script>
